import React from 'react'
import { dateDiffDays, formatStartCollapsedBadge } from '../../utils/parseStartDate'
import { TodayIcon, TomorrowIcon, PeriodIcon } from '../WhenDropdown'
import './TaskStartDateBadge.scss'
import type { TaskStartDateBadgeProps } from './TaskStartDateBadge.types'

export function TaskStartDateBadge({ date, locale, period, labelMode = 'auto' }: TaskStartDateBadgeProps) {
  const now = new Date()
  const diff = dateDiffDays(date, now)
  const isDE = locale.startsWith('de')

  // Today / Tomorrow render as an icon-only badge.
  // For Today with a period, the period icon replaces the day icon
  // (e.g. evening icon alone communicates "this evening"). Tomorrow
  // always keeps its day icon — the period is implied by context.
  if (diff === 0) {
    if (period) return <PeriodIcon period={period} size={16} />
    return <TodayIcon size={16} />
  }
  if (diff === 1) return <TomorrowIcon size={16} />

  if (diff < 0) {
    const absDiff = -diff
    let label: string
    if (absDiff === 1) {
      label = isDE ? 'Gestern' : 'Yesterday'
    } else if (absDiff >= 7) {
      const weeks = Math.floor(absDiff / 7)
      label = isDE ? `Vor ${weeks} Wo.` : `${weeks}w ago`
    } else {
      label = isDE ? `Vor ${absDiff} Tagen` : `${absDiff} days ago`
    }
    return <span className="task-start-date-badge task-start-date-badge--overdue">{label}</span>
  }

  // Future (diff >= 2). Absolute label by default, relative on opt-in —
  // used e.g. on the UpcomingPage where the surrounding day list already
  // communicates the absolute date.
  if (labelMode === 'relative') {
    let label: string
    if (diff >= 7) {
      const weeks = Math.floor(diff / 7)
      label = isDE ? `In ${weeks} Wo.` : `In ${weeks}w`
    } else {
      label = isDE ? `In ${diff} Tagen` : `In ${diff} days`
    }
    return <span className="task-start-date-badge">{label}</span>
  }

  const label = formatStartCollapsedBadge(date, locale, now)
  return <span className="task-start-date-badge">{label}</span>
}
